import intersection from 'lodash-es/intersection';

import type { MapViewLayout } from 'cadenza/model/map-view-layout';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key';
import { getSettingOrSettingDefault } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { ViewMetadata, ViewWithMetadata, WorkbookState } from 'cadenza/workbook/workbook.state';
import {
  getActiveWorksheetViews,
  getCouplingLayerLimit,
  getEmbeddingTargets,
  getViewById,
  getViewMetadata,
  getViews,
  removeZoomRelatedViewId,
  setTargetMapExtentAndScaleForCoupledView,
  slice,
  stopInteractionForCoupledView,
  synchronizeToLeftTopMostView,
} from 'cadenza/workbook/workbook.state';
import type { MapCouplingGroupDto, MapCouplingGroupId } from 'cadenza/workbook/model/map-coupling-group-dto';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import type { ExecuteCommandResult } from 'cadenza/api-client/workbook-api';
import { colStart, rowStart } from 'cadenza/workbook/worksheet/dto/grid-area-dto';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import { keys } from 'cadenza/utils/type-util';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { validateMapViewLayers } from 'cadenza/workbook/view-validation/view-validation';
import { assert, assertNonNullable } from 'cadenza/utils/custom-error';
import { isClassicLayer } from 'cadenza/workbook/map/workbook-layer-type-util';

import type { PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';
import type { Extent } from 'ol/extent';

export interface MapViewMetadata extends ViewMetadata {
  mapExtent?: WorldBoxDto; // the map extent that is updated while interacting with the map (zooming, moving etc.) (see also MapViewContextDto.currentExtent)
  mapScale?: number;
  visibleRangeByLayerId?: Record<LayerId, boolean>;
}

export interface MapViewWorkbookState {
  /**
   * Holds configuration and transient state of map coupling groups (map views that try
   * to synchronize either scale or extent).
   * Coupled map views reference their group id.
   */
  mapCouplingGroupsById: Record<MapCouplingGroupId, MapCouplingGroupWithState>;
  zoomRelatedViewIds?: ViewId[];
  /**
   * Used for communication between the main map view and the overview map view.
   * It should keep the current state of the view, synchronized with UI view changes like e.g. zooming (in contrary to view.mapViewContext.currentExtent which is set from the backend).
   */
  overviewMapContextsByMainMapViewId: Record<ViewId, OverviewMapContext>;
}

/**
 * Extends map coupling group by a transient state of current target scale/extent.
 */
export interface MapCouplingGroupWithState extends MapCouplingGroupDto {
  /** Target extent for coupled views */
  extent?: Extent;
  /** Target scale for coupled views */
  scale?: number;
  /** View that set the new target values. Used to avoid feedback loops of changed loop */
  initiator?: ViewId;
  animatingOrInteracting?: boolean;
  isLayerLimitExceeded: boolean;
}

interface OverviewMapContext {
  currentExtent: Extent;
}

export function createInitialMapViewState() {
  return {
    mapCouplingGroupsById: {},
    overviewMapContextsByMainMapViewId: {},
  };
}

export function patchZoomRelatedViewsState(state: WorkbookState, result: ExecuteCommandResult) {
  // If the ExecuteCommandResult does not include new auto-zoom views,
  // we keep existing ones: If there are some, then the auto-zoom was not done, yet.
  state.zoomRelatedViewIds =
    result.viewsToZoomToDataExtent?.length ?
      intersection(result.viewsToZoomToDataExtent, keys(state.viewById))
    : state.zoomRelatedViewIds;
}

export function patchMapViewState(state: WorkbookState, result: ExecuteCommandResult) {
  patchZoomRelatedViewsState(state, result);
  validateMapViewLayers(state);
  updateOverviewMapContextsByMainMapViewId(state);
}

export function initializeOverviewMapContextsByMainMapViewId(state: RootStateOr<WorkbookState>) {
  // only for initializing (we need the initial extents from main map views for report printing)
  getViews(state)
    .filter((view) => isOverviewMap(state, view.id))
    .forEach((view) => {
      const viewId: ViewId = view.id;
      const overviewMapViewContext = getMapViewContext(state, viewId);
      const mainMapViewId = overviewMapViewContext.overviewMainMapViewId;
      assertNonNullable(mainMapViewId);
      const mainMapViewContext = getViewById(state, mainMapViewId).mapViewContext;
      const currentExtent = mainMapViewContext?.currentExtent as Extent;
      slice(state).overviewMapContextsByMainMapViewId[mainMapViewId] = Object.assign(
        getOverviewMapContextByMainMapViewId(slice(state), mainMapViewId) ?? {},
        { currentExtent },
      );
    });
}

function updateOverviewMapContextsByMainMapViewId(state: WorkbookState) {
  // remove all views that are no longer main map views
  Object.keys(state.overviewMapContextsByMainMapViewId).forEach((key) => {
    const viewId = key as ViewId;
    if (!isMainMapView(state, viewId as ViewId)) {
      delete state.overviewMapContextsByMainMapViewId[viewId];
    }
  });
  // the new entries are added only from main map interactions with the state (we need calculated extent, not the one from the backend,
  // because it would be not always correct) => see setMainMapContextForOverviewMap calls
}

export const mapViewReducers = {
  setTargetMapExtentAndScaleForCoupledView(
    state: RootStateOr<WorkbookState>,
    {
      payload: { viewId, extent, scale, animatingOrInteracting = false, isLayerLimitExceeded = false },
    }: PayloadAction<{
      viewId: ViewId;
      extent: WorldBoxDto;
      scale: number;
      animatingOrInteracting?: boolean;
      isLayerLimitExceeded?: boolean;
    }>,
  ) {
    const group = getMapCouplingGroupWithState(state, viewId);
    assert(group, 'Map view not part of coupling group');

    Object.assign(group, {
      scale,
      extent,
      initiator: viewId,
      animatingOrInteracting,
      isLayerLimitExceeded,
    });
  },
  stopInteractionForCoupledView(
    state: RootStateOr<WorkbookState>,
    { payload: { viewId } }: PayloadAction<{ viewId: ViewId }>,
  ) {
    const group = getMapCouplingGroupWithState(state, viewId);
    assert(group, 'Map view not part of coupling group');

    group.animatingOrInteracting = false;
  },
  synchronizeToLeftTopMostView(
    state: RootStateOr<WorkbookState>,
    { payload: { groupId } }: PayloadAction<{ groupId: MapCouplingGroupId }>,
  ) {
    const mostLeftTopView = getMostLeftTopViewOfGroup(state, groupId);
    const metadata = getViewMetadata<MapViewMetadata>(state, mostLeftTopView.id)!;
    const group = getMapCouplingGroupWithStateById(state, groupId);

    Object.assign(group, {
      initiator: mostLeftTopView.id,
      scale: metadata.mapScale!,
      extent: metadata.mapExtent!,
      isLayerLimitExceeded: false,
    });
  },
  removeZoomRelatedViewId(state, { payload }: PayloadAction<ViewId>) {
    state.zoomRelatedViewIds = getZoomRelatedViewIds(state).filter((id) => id !== payload);
  },
  setMainMapContextForOverviewMap(
    state: RootStateOr<WorkbookState>,
    { payload }: PayloadAction<{ mainMapViewId: ViewId; extent: Extent }>,
  ) {
    const { mainMapViewId, extent } = payload;
    const context = getOverviewMapContextByMainMapViewId(slice(state), mainMapViewId);
    slice(state).overviewMapContextsByMainMapViewId[mainMapViewId] = Object.assign(context ?? {}, {
      currentExtent: extent,
    });
  },
  // Takes viewId of overview map and extent of overview map and sets that extent on corresponding main map (used on overview map interaction)
  setMainMapExtentForOverviewMapViewId(
    state: RootStateOr<WorkbookState>,
    { payload }: PayloadAction<{ overviewMapViewId: ViewId; extent: Extent }>,
  ) {
    const { overviewMapViewId, extent } = payload;
    const mainMapViewId = getOverviewMainMapViewId(state, overviewMapViewId);
    assertNonNullable(mainMapViewId, `${overviewMapViewId} is not a valid ID of an overview map view`);
    Object.assign(getOverviewMapContextByMainMapViewId(slice(state), mainMapViewId) ?? {}, { currentExtent: extent });
  },
} satisfies SliceCaseReducers<WorkbookState>;

export {
  setTargetMapExtentAndScaleForCoupledView,
  stopInteractionForCoupledView,
  synchronizeToLeftTopMostView,
  removeZoomRelatedViewId,
};

export function getMapCouplingGroupWithStateById(
  state: RootStateOr<WorkbookState>,
  couplingGroupId: MapCouplingGroupId,
) {
  return slice(state).mapCouplingGroupsById[couplingGroupId];
}

export function getMapCouplingGroupWithState(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  // ?. because view might already be removed from state when executing this in an observer
  const groupId = getViewById(state, viewId)?.mapViewContext?.mapCouplingGroupId;
  return groupId ? getMapCouplingGroupWithStateById(state, groupId) : undefined;
}

export function getMapViewContext(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const view = getViewById(state, viewId);
  assert(view.type === 'MAP', 'Map context is available only for map views.');
  assertNonNullable(view.mapViewContext);
  return view.mapViewContext;
}

export function getMostLeftTopViewOfGroup(state: RootStateOr<WorkbookState>, groupId: MapCouplingGroupId) {
  const views = getViewsOfMapCouplingGroup(state, groupId);
  return getMostLeftTopView(views);
}

export function getViewsOfMapCouplingGroup(state: RootStateOr<WorkbookState>, groupId: MapCouplingGroupId) {
  return getViews(state).filter((view) => view.mapViewContext?.mapCouplingGroupId === groupId);
}

function getMostLeftTopView(views: ViewDto[]) {
  return views.reduce((currentMostLeftTopView, candidateView) => {
    const currentMostLeftTopViewsGridArea = currentMostLeftTopView.layout;
    const candidateViewsGridArea = candidateView.layout;

    const isMoreLeft = colStart(candidateViewsGridArea) < colStart(currentMostLeftTopViewsGridArea);
    const isEqualLeft = colStart(candidateViewsGridArea) === colStart(currentMostLeftTopViewsGridArea);
    const isMoreTop = rowStart(candidateViewsGridArea) < rowStart(currentMostLeftTopViewsGridArea);

    return isMoreLeft || (isEqualLeft && isMoreTop) ? candidateView : currentMostLeftTopView;
  });
}

export function getZoomRelatedViewIds(state: RootStateOr<WorkbookState>) {
  return slice(state).zoomRelatedViewIds ?? [];
}

export const getMapViewSrs = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewContext(state, viewId).projection.code;

export const getMapViewMinAllowedScale = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewContext(state, viewId).minAllowedScale;

export const getMapViewMaxAllowedScale = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewContext(state, viewId).maxAllowedScale;
/** Returns the overview map for a potential main map, otherwise undefined **/
export const getOverviewMap = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getActiveWorksheetViews(state).find((view) => view.mapViewContext?.overviewMainMapViewId === viewId);

/** Returns the id of the main map for a potential overview map, otherwise undefined **/
export const getOverviewMainMapViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMapViewContext(state, viewId).overviewMainMapViewId;

/** Returns true if the view is the main map for an overview map, otherwise false **/
export const isMainMapView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  Boolean(getOverviewMap(state, viewId));

/** Returns true if the view is an overview map and thus has a reference to its main map **/
export const isOverviewMap = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  Boolean(getViewById(state, viewId)?.mapViewContext?.overviewMainMapViewId);

export const hasLegacyOverviewMap = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return !!getViewById(state, viewId)?.mapViewContext?.locatorScaleMode;
};

export const getOverviewMainMapMaxExtentByOverviewMapViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const overviewMainMapViewId = getOverviewMainMapViewId(state, viewId);
  if (overviewMainMapViewId) {
    return getMapViewContext(state, overviewMainMapViewId).maxExtent;
  }
};

const getOverviewMapContextByMainMapViewId = (
  state: RootStateOr<WorkbookState>,
  viewId?: ViewId,
): OverviewMapContext | undefined => {
  const overviewMapContext = slice(state).overviewMapContextsByMainMapViewId;
  if (overviewMapContext && viewId) {
    return overviewMapContext[viewId];
  }
  return undefined;
};

export const getOverviewMainMapCurrentExtentByOverviewMapViewId = (
  state: RootStateOr<WorkbookState>,
  overviewMapViewId: ViewId,
) => {
  const mainMapViewId = getOverviewMainMapViewId(state, overviewMapViewId);
  return getOverviewMapContextByMainMapViewId(state, mainMapViewId)?.currentExtent;
};

export const getOverviewMapCurrentExtentByMainMapViewId = (
  state: RootStateOr<WorkbookState>,
  mainMapViewId: ViewId,
) => {
  return getOverviewMapContextByMainMapViewId(state, mainMapViewId)?.currentExtent;
};

export const getSuitableOverviewMapsForView = (
  state: RootStateOr<WorkbookState>,
  mainMapCandidateId: ViewId,
): ViewWithMetadata[] => {
  const srs = getMapViewSrs(state, mainMapCandidateId);
  return getActiveWorksheetViews(state).filter((view) => {
    if (view.type !== 'MAP' || view.id === mainMapCandidateId) {
      return false; // Not a map or main map itself
    }
    const mapViewContext = getMapViewContext(state, view.id);
    const usesSameSrs = mapViewContext.projection.code === srs;
    const overviewMainMapViewId = mapViewContext.overviewMainMapViewId;
    const isOverviewForOtherView = overviewMainMapViewId && overviewMainMapViewId !== mainMapCandidateId;
    const isMainMapViewForOtherView = isMainMapView(state, view.id);
    const isCoupled = mapViewContext.mapCouplingGroupId != null;
    const hasClassicLayers = Object.values(view.layerDefinitions).some(isClassicLayer);
    const isUsedAsEmbedding = getEmbeddingTargets(state).some(
      ({ viewId: embeddingViewId }) => embeddingViewId === view.id,
    );
    return (
      usesSameSrs &&
      !isOverviewForOtherView &&
      !isCoupled &&
      !hasClassicLayers &&
      !isUsedAsEmbedding &&
      !isMainMapViewForOtherView
    );
  });
};

export const isMapLayerInVisibleRange = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) =>
  getViewMetadata<MapViewMetadata>(state, viewId)?.visibleRangeByLayerId?.[layerId] ?? true;

export const isCouplingLayerLimitExceededForGroupByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const mapCouplingGroupId = getMapViewContext(state, viewId).mapCouplingGroupId;
  return mapCouplingGroupId && isCouplingLayerLimitExceededForGroup(state, mapCouplingGroupId);
};

export const isCouplingLayerLimitExceededForGroup = (
  state: RootStateOr<WorkbookState>,
  groupId: MapCouplingGroupId,
) => {
  const groupLayerCount = getCouplingLimitRelevantLayerCountOfGroup(state, groupId);
  const couplingLayerLimit = getCouplingLayerLimit(state);
  return groupLayerCount > couplingLayerLimit;
};

/**
 * Returns the count of layers of the given map view, relevant for coupling of map views.
 * If already coupled, it will return the layer count for the group, of only the view otherwise.
 */
export function getCouplingLimitRelevantLayerCountOfViewOrGroup(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  const mapCouplingGroupId = getMapViewContext(state, viewId).mapCouplingGroupId;
  return mapCouplingGroupId ?
      getCouplingLimitRelevantLayerCountOfGroup(state, mapCouplingGroupId)
    : getCouplingLimitRelevantLayerCountOfView(getViewById(state, viewId));
}

function getCouplingLimitRelevantLayerCountOfGroup(state: RootStateOr<WorkbookState>, groupId: MapCouplingGroupId) {
  return getViewsOfMapCouplingGroup(state, groupId)
    .map((view) => getCouplingLimitRelevantLayerCountOfView(view))
    .reduce((sum, layerCount) => sum + layerCount, 0);
}

/**
 * 1. Far all layers except group layers in map view the sum is increased by one (independent of layer visibility)
 * 2. For standard group layers the sum is increased by calculating the sum of all nested layers by using rules 1,2 and 3 (independent of layer visibility)
 * 3. For toggle layers the sum is increased by the maximum sum of all nested layers by using rules 1,2,3 (independent of layer visibility)
 *
 * @param view - view, whose layers will be counted
 */
export function getCouplingLimitRelevantLayerCountOfView(view: ViewDto) {
  const layerTree = view.layerTree;
  const layerDefinitions = view.layerDefinitions;
  return couplingLimitRelevantLayerCountOfView(layerTree, layerDefinitions);
}

function couplingLimitRelevantLayerCountOfView(
  layerTree: WorkbookLayerTreeNode[],
  layerDefinitions: Record<LayerId, WorkbookLayerDto>,
  isChildOfToggleGroup: boolean = false,
): number {
  return layerTree.reduce((sum, layer) => {
    if (layerDefinitions[layer.id].structureType === 'GROUP') {
      return sum + couplingLimitRelevantLayerCountOfView(layer.children, layerDefinitions);
    }
    if (layerDefinitions[layer.id].structureType === 'TOGGLE') {
      return (
        sum +
        layer.children.reduce((max, child) => {
          if (child.children.length) {
            const count = couplingLimitRelevantLayerCountOfView(
              child.children,
              layerDefinitions,
              layerDefinitions[child.id].structureType === 'TOGGLE',
            );
            return Math.max(max, count);
          }
          return max;
        }, 1)
      );
    }
    return isChildOfToggleGroup ? Math.max(sum, 1) : sum + 1;
  }, 0);
}

export const getMapViewLayoutSetting = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getSettingOrSettingDefault<MapViewLayout>(
    getViewById(state, viewId),
    MapViewSettingsKey.toPersistenceKey('LAYOUT'),
  );
};

export const getLegendAndOverviewMapSettings = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  return {
    isLegendEnabled: getSettingOrSettingDefault<boolean>(view, MapViewSettingsKey.toPersistenceKey('LEGEND_ENABLED')),
    isOverviewMapEnabled: getSettingOrSettingDefault<boolean>(
      view,
      MapViewSettingsKey.toPersistenceKey('OVERVIEW_MAP_ENABLED'),
    ),
  };
};
